import { Form, Formik, FormikErrors, FormikHelpers, FormikTouched } from 'formik';
import React from 'react';
import * as Yup from 'yup';
import { NavigationFooter } from 'src/components';
import { Wizard } from 'src/components/Wizard';
import HeaderNav from 'src/containers/self-service/header-nav/header-nav';
import { v4 as generateUUID } from 'uuid';
import useMerchantProfile from 'src/hooks/useMerchantProfile';
import { isValidMobileNumber } from 'src/helpers/validation';
import {
  NotificationMethod,
  PaymentResult,
  PaymentScope,
  PaymentType,
  paymentRequests,
} from 'src/services/payment-request-service';
import { Loader } from 'src/containers/self-service/generate-payment-request/components/Loader';
import { TechnicalErrorModal } from 'src/containers/self-service/generate-payment-request/components/TechnicalErrorModal';
import { stripWhitespace } from 'src/helpers/formatting';
import { PaymentRequestTealiumService } from 'src/containers/self-service/generate-payment-request/tagging/payment-request-tealium-service';
import { getSummaryPageName } from 'src/containers/self-service/generate-payment-request/components/Summary';

export type Page = React.ReactFragment;

export type GeneratePaymentRequestWizardPageProps = {
  setOnNextEventHandler: (handleEvent: EventHandler) => void;
  setOnBackEventHandler: (handleEvent: EventHandler) => void;
  formValues: InitValues;
  setCurrentIndex: (index: number) => void;
  isLoading: boolean;
  setIsLoading: (state: boolean) => void;
  notificationAddress: string;
  notificationMethod: NotificationMethod;
  paymentType: PaymentType;
  paymentResult?: PaymentResult;
  submitForm: (() => Promise<void>) & (() => Promise<any>);
  validateForm: (values?: InitValues) => Promise<FormikErrors<InitValues>>;
  setTouched: (touched: FormikTouched<InitValues>, shouldValidate?: boolean | undefined) => void;
};

type EventHandler = () => any;

const defaultEventHandler = () => {};

export type GeneratePaymentRequestWizardPageStep = {
  page: (props: GeneratePaymentRequestWizardPageProps) => Page;
  name: string;
};

export type GeneratePaymentRequestWizardProps = {
  steps: GeneratePaymentRequestWizardPageStep[];
  currentStepIndex: number;
  setCurrentStepIndex: (index: number) => void;
};

export enum ButtonLabel {
  'Next',
  'Generate',
  'Send',
  'Done',
}

interface InitValues {
  categoryType: PaymentType;
  notificationMethod: NotificationMethod;
  notificationAddress: string;
  transactionReference: string;
  transactionAmount: string;
}

export function GeneratePaymentRequestWizard({
  steps,
  currentStepIndex,
  setCurrentStepIndex,
}: GeneratePaymentRequestWizardProps) {
  const { merchant } = useMerchantProfile();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [notificationAddress, setNotificationAddress] = React.useState('');
  const [paymentType, setPaymentType] = React.useState<PaymentType>(PaymentType.None);
  const [notificationMethod, setNotificationMethod] = React.useState<NotificationMethod>(
    NotificationMethod.None,
  );
  const [showTechError, setShowTechError] = React.useState(false);
  const [error, setError] = React.useState<Error>();
  const currentStep = steps[currentStepIndex];
  const [isLoading, setIsLoading] = React.useState(false);
  const [paymentInfo, setPaymentInfo] = React.useState<PaymentResult | undefined>(undefined);
  const onNextRef = React.useRef<EventHandler>(defaultEventHandler);
  const onBackRef = React.useRef<EventHandler>(defaultEventHandler);
  const initialValues: InitValues = {
    notificationAddress: '',
    categoryType: PaymentType.None,
    notificationMethod: NotificationMethod.Email,
    transactionReference: '',
    transactionAmount: '',
  };

  function setOnNextEventHandler(handleEvent: EventHandler) {
    onNextRef.current = handleEvent;
  }

  function setOnBackEventHandler(handleEvent: EventHandler) {
    onBackRef.current = handleEvent;
  }

  function handleNavigateNext() {
    if (onNextRef.current) {
      onNextRef.current();
      setCurrentStepIndex(currentStepIndex < steps.length - 1 ? currentStepIndex + 1 : 0);
    }
  }

  function handleNavigateBack() {
    if (onBackRef.current) {
      onBackRef.current();
      setCurrentStepIndex(currentStepIndex > 0 ? currentStepIndex - 1 : 0);
    }
  }

  const wizardSteps = steps.map((step, index) => ({ label: step.name, substeps: index }));

  const generatePaymentRequestSchema = Yup.object({
    transactionAmount: Yup.string().when('categoryType', {
      is: (category: string) => PaymentType.FixedAmount === category && currentStepIndex === 1,
      then: (schema) => schema.required('Transaction amount is required'),
    }),
    categoryType: Yup.string().required(),
    notificationAddress: Yup.string().when('notificationMethod', {
      is: (notificationMethod: string) =>
        NotificationMethod.Email === notificationMethod && currentStepIndex === 1,
      then: (schema) =>
        schema.email('Please use a valid email address.').required('Email address is required.'),
      otherwise: (schema) =>
        currentStepIndex === 1
          ? schema
              .required('Mobile number is required.')
              .test('isvalid-number', 'Please enter a valid mobile number', (number) =>
                isValidMobileNumber(stripWhitespace(number)),
              )
          : schema.nullable(),
    }),
    transactionReference: Yup.string().when('categoryType', {
      is: (category: string) => PaymentType.FixedAmount === category && currentStepIndex === 1,
      then: (schema) => schema.required('Transaction reference is required.'),
    }),
  });

  const handleSubmit = async (values: InitValues, helpers: FormikHelpers<InitValues>) => {
    try {
      setIsLoading(true);
      setIsSubmitting(true);
      setNotificationAddress(values.notificationAddress);
      setNotificationMethod(values.notificationMethod);
      setPaymentType(values.categoryType);
      const amountInCents = values.transactionAmount
        ? Number(values.transactionAmount.trim().replace(/\s/g, '')) * 100
        : undefined;

      const response = await paymentRequests(
        {
          sourceTransactionId: generateUUID(),
          paymentScope: PaymentScope.SingleCustomer,
          reference: values.transactionReference,
          amountInCents,
          notificationType: values.notificationMethod,
          notificationAddress: stripWhitespace(values.notificationAddress),
          paymentType: values.categoryType,
        },
        merchant.merchantId ?? '',
      );

      if (response.paymentLink) {
        setIsLoading(false);
        setPaymentInfo(response);
        helpers.resetForm();
      }
    } catch (error) {
      setError(error as Error);
      setShowTechError(true);
      PaymentRequestTealiumService.onTechnicalError(
        '500',
        getSummaryPageName(values.categoryType, values.notificationMethod),
      );
    } finally {
      setIsLoading(false);
      setIsSubmitting(false);
    }
  };

  const submit = async (values: InitValues, helpers: FormikHelpers<InitValues>) => {
    if (currentStepIndex === 3 && !isSubmitting) {
      await handleSubmit(values, helpers);
      return;
    }
  };

  return (
    <article className="GeneratePaymentRequestWizard [&_.divider]:bg-grey-700">
      <Formik
        onSubmit={submit}
        initialValues={initialValues}
        validateOnBlur
        validateOnMount
        validateOnChange
        enableReinitialize
        validationSchema={generatePaymentRequestSchema}
      >
        {({ isValid, values, errors, validateForm, submitForm, setTouched }) => {
          if (Object.keys(errors).length === 3 && currentStepIndex === 1) {
            PaymentRequestTealiumService.onFormError(
              values.categoryType,
              'form validation',
              'form validation',
            );
          }

          return (
            <Form>
              <TechnicalErrorModal
                error={error}
                visible={showTechError}
                onRetry={() => {
                  setShowTechError(false);
                  setError(undefined);
                  submitForm();
                }}
                onClose={() => setShowTechError(false)}
              />
              <HeaderNav
                title={merchant?.companyName ? `Hi, welcome back ${merchant?.companyName}` : ''}
                screenName="Generate Payment Request"
              >
                <section className="md:px-20 [&_.stepper-label-active]:text-white [&_.stepper-ball-step_img]:h-3 [&_.stepper-ball-step_img]:w-3 [&_.stepper-container]:w-full [&_.stepper-ball-step]:w-[30px] [&_.stepper-ball-step]:font-medium [&_.stepper-ball-step]:text-sm [&_.stepper-ball-step]:h-[30px] [&_.stepper-ball-step]:border-black">
                  {isLoading ? (
                    <Loader />
                  ) : (
                    <>
                      <Wizard
                        steps={wizardSteps}
                        currentStep={currentStepIndex}
                        currentSubStep={currentStepIndex}
                        onBackButton={() => {
                          handleNavigateBack();
                          validateForm();
                        }}
                        hideBackButton={currentStepIndex <= 0 || currentStepIndex === 3}
                      />
                      {currentStep.page({
                        setOnBackEventHandler,
                        formValues: values,
                        paymentResult: paymentInfo,
                        setOnNextEventHandler,
                        setCurrentIndex: setCurrentStepIndex,
                        isLoading,
                        setIsLoading,
                        submitForm,
                        setTouched,
                        validateForm,
                        notificationAddress,
                        notificationMethod,
                        paymentType,
                      })}
                    </>
                  )}
                </section>
              </HeaderNav>
              <NavigationFooter
                onBack={handleNavigateBack}
                onNext={handleNavigateNext}
                onCancel={() => {}} /* @ts-ignore */
                onSave={() => {}}
                showBasketIcon={false}
                showBasket={false}
                setShowBasket={() => {}}
                isBasketMiniMode={false}
                nextEnabled={isValid}
                hideNext={false}
                hideBack={currentStepIndex <= 0 || currentStepIndex === 3}
                hideCancel={true}
                nextButtonLabel={ButtonLabel[currentStepIndex]}
                hideSaveForLater={true}
              />
            </Form>
          );
        }}
      </Formik>
    </article>
  );
}
